import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  DialogContentText,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import type React from 'react';
import { useEffect, useState } from 'react';
import { getUsers } from '../../services/api';
import { assignEvaluator, removeEvaluatorAssignment } from '../../services/procurementService';
import type { EvaluatorAssignment, Supplier } from '../../types/procurement';
import { BaseModal } from '../common/UI/BaseModal';

interface Props {
  open: boolean;
  onClose: () => void;
  procurementId: string;
  supplierId: string;
  supplierName: string;
  evaluatorAssignments: EvaluatorAssignment[];
  allSuppliers: Supplier[];
  allAssignments: EvaluatorAssignment[];
  onShowSnackbar: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
}

const AssignEvaluatorModal: React.FC<Props> = ({
  open,
  onClose,
  procurementId,
  supplierId,
  supplierName,
  evaluatorAssignments,
  allSuppliers,
  allAssignments,
  onShowSnackbar,
}) => {
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [isAssigning, setIsAssigning] = useState(false);
  const [assignToAll, setAssignToAll] = useState(false);

  const [usersList, setUsersList] = useState<{ id: string; name: string }[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [assignmentToDelete, setAssignmentToDelete] = useState<EvaluatorAssignment | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    let isMounted = true;
    if (open) {
      setIsLoadingUsers(true);
      getUsers({ limit: 100 })
        .then((res) => {
          if (isMounted && res.users) {
            setUsersList(
              res.users
                .filter((u) => !u.disabled && u.status !== 'pending')
                .map((u) => ({
                  id: u.uid,
                  name: u.displayName || u.email || 'Unknown User',
                }))
            );
          }
        })
        .finally(() => {
          if (isMounted) {
            setIsLoadingUsers(false);
          }
        });
    }
    return () => {
      isMounted = false;
    };
  }, [open]);

  // Users who haven't been assigned to this supplier yet
  const availableUsers = usersList.filter(
    (u) => !evaluatorAssignments.some((ea) => ea.userId === u.id)
  );

  const handleAssign = async () => {
    if (!selectedUser) {
      return;
    }

    setIsAssigning(true);
    const user = usersList.find((u) => u.id === selectedUser);

    if (user) {
      if (assignToAll) {
        const promises = allSuppliers.map(async (sup) => {
          const isAssigned = allAssignments.some(
            (ea) => ea.supplierId === sup.id && ea.userId === user.id
          );
          if (!isAssigned) {
            return assignEvaluator(procurementId, sup.id, user.id, user.name);
          }
          return { success: true, error: undefined };
        });

        const results = await Promise.all(promises);
        const hasError = results.some((r) => !r.success);
        if (hasError) {
          onShowSnackbar(
            `Assigned ${user.name} to some suppliers, but errors occurred.`,
            'warning'
          );
        } else {
          onShowSnackbar(`Assigned ${user.name} to all suppliers.`, 'success');
          onClose();
        }
      } else {
        const res = await assignEvaluator(procurementId, supplierId, user.id, user.name);
        if (res.success) {
          onShowSnackbar(`Assigned ${user.name} to evaluate ${supplierName}.`, 'success');
          onClose();
        } else {
          onShowSnackbar(`Failed to assign evaluator: ${res.error}`, 'error');
        }
      }
    }

    setIsAssigning(false);
  };

  const handleRemove = async () => {
    if (!assignmentToDelete) {
      return;
    }

    setIsDeleting(true);
    const res = await removeEvaluatorAssignment(procurementId, assignmentToDelete.id);
    if (res.success) {
      onShowSnackbar(`Removed evaluator ${assignmentToDelete.userName}.`, 'success');
      onClose();
    } else {
      onShowSnackbar(`Failed to remove evaluator: ${res.error}`, 'error');
    }
    setIsDeleting(false);
    setAssignmentToDelete(null);
  };

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title="Assign Evaluator"
      maxWidth="sm"
      headerSx={{ bgcolor: 'action.hover' }}
    >
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Typography variant="body1">
          Select a team member to evaluate the submission from <strong>{supplierName}</strong>.
        </Typography>

        {evaluatorAssignments.length > 0 && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Currently Assigned
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell>Name</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {evaluatorAssignments.map((ea) => (
                    <TableRow key={ea.id}>
                      <TableCell>{ea.userName}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Remove Assignment">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setAssignmentToDelete(ea)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        <FormControl fullWidth>
          <InputLabel>Select Evaluator</InputLabel>
          <Select
            value={selectedUser}
            label="Select Evaluator"
            onChange={(e) => setSelectedUser(e.target.value as string)}
            disabled={isLoadingUsers}
          >
            {isLoadingUsers ? (
              <MenuItem disabled value="">
                <CircularProgress size={20} sx={{ mr: 2 }} /> Loading users...
              </MenuItem>
            ) : availableUsers.length === 0 ? (
              <MenuItem disabled value="">
                All team members assigned
              </MenuItem>
            ) : (
              availableUsers.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.name}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>

        <FormControlLabel
          control={
            <Checkbox checked={assignToAll} onChange={(e) => setAssignToAll(e.target.checked)} />
          }
          label="Assign this evaluator to all suppliers"
        />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
          <Button variant="outlined" onClick={onClose} disabled={isAssigning}>
            Done
          </Button>
          <Button
            variant="contained"
            onClick={handleAssign}
            disabled={!selectedUser || isAssigning}
          >
            {isAssigning ? 'Assigning...' : 'Assign Evaluator'}
          </Button>
        </Box>
      </Box>

      {/* Delete Confirmation Modal */}
      <BaseModal
        open={Boolean(assignmentToDelete)}
        onClose={() => setAssignmentToDelete(null)}
        title="Remove Evaluator"
        actions={
          <>
            <Button onClick={() => setAssignmentToDelete(null)} color="inherit">
              Cancel
            </Button>
            <Button
              onClick={handleRemove}
              color="error"
              variant="contained"
              disabled={isDeleting}
              startIcon={isDeleting && <CircularProgress size={16} color="inherit" />}
            >
              {isDeleting ? 'Removing...' : 'Remove'}
            </Button>
          </>
        }
      >
        <DialogContentText>
          Are you sure you want to remove <strong>{assignmentToDelete?.userName}</strong> as an
          evaluator for {supplierName}?
        </DialogContentText>
      </BaseModal>
    </BaseModal>
  );
};

export default AssignEvaluatorModal;
